/**
 * `telegram/useTelegramSafeArea.ts` — Telegram's insets as two CSS variables
 * (design pass #2071).
 *
 * In full screen on a phone, Telegram draws the status bar and its own Close
 * (or Back) and ⋯ buttons over the top of the page, and the home indicator
 * over the bottom. `env(safe-area-inset-*)` does not know about Telegram's
 * buttons, so the SDK reports both bands: `safeAreaInset` (the device's) and
 * `contentSafeAreaInset` (Telegram's buttons). Their sums land on
 * `--tarii-tg-inset-top` / `--tarii-tg-inset-bottom`, which the `safe-top` /
 * `safe-bottom` spacing (tailwind.config.ts) takes the larger of with `env()`
 * — so the app's top row sits under Telegram's buttons and the tab bar above
 * the home indicator, and nothing changes outside Telegram (the variables are
 * unset there). Re-read whenever Telegram says the insets or the mode moved.
 *
 * The hook mounts before `AuthContext` has loaded Telegram's script, so it
 * waits for the SDK (`whenTelegramWebApp`) before it reads and subscribes
 * (#2176) — reading at mount found nothing, left both variables at 0 px and
 * the top row under Telegram's buttons.
 */
import { useEffect, useState } from "react";

import { type TelegramWebApp, telegramWebApp, whenTelegramWebApp } from "./telegramSdk";

export const INSET_TOP_VAR = "--tarii-tg-inset-top";
export const INSET_BOTTOM_VAR = "--tarii-tg-inset-bottom";

const EVENTS = [
  "safeAreaChanged",
  "contentSafeAreaChanged",
  "fullscreenChanged",
  "viewportChanged",
];

const px = (n: number | undefined) => (typeof n === "number" && Number.isFinite(n) ? n : 0);

/** Telegram's top / bottom bands in CSS pixels (device + Telegram's buttons). */
export function telegramInsets(app: TelegramWebApp | null): { top: number; bottom: number } {
  return {
    top: px(app?.safeAreaInset?.top) + px(app?.contentSafeAreaInset?.top),
    bottom: px(app?.safeAreaInset?.bottom) + px(app?.contentSafeAreaInset?.bottom),
  };
}

function applyInsets(app: TelegramWebApp | null): void {
  if (typeof document === "undefined") return;
  const { top, bottom } = telegramInsets(app);
  const style = document.documentElement.style;
  style.setProperty(INSET_TOP_VAR, `${top}px`);
  style.setProperty(INSET_BOTTOM_VAR, `${bottom}px`);
}

/** Keep the two inset variables in step with Telegram while mounted. */
export function useTelegramSafeArea(): void {
  useEffect(() => {
    let app: TelegramWebApp | null = null;
    const onChange = () => applyInsets(app);
    const cancel = whenTelegramWebApp((loaded) => {
      app = loaded;
      applyInsets(loaded);
      for (const event of EVENTS) loaded.onEvent?.(event, onChange);
    });
    return () => {
      cancel();
      for (const event of EVENTS) app?.offEvent?.(event, onChange);
      document.documentElement.style.removeProperty(INSET_TOP_VAR);
      document.documentElement.style.removeProperty(INSET_BOTTOM_VAR);
    };
  }, []);
}

/** Telegram's own button band in full screen, in CSS px: it starts `top` px
 *  down (under the status bar) and is `height` tall. */
export interface TelegramTopBand {
  top: number;
  height: number;
}

/** The band Telegram floats Close and ⌄ ⋯ in — only in full screen, and only
 *  when Telegram reports one (`contentSafeAreaInset.top` > 0). */
export function telegramTopBand(app: TelegramWebApp | null): TelegramTopBand | null {
  if (!app?.isFullscreen) return null;
  const height = px(app.contentSafeAreaInset?.top);
  if (height <= 0) return null;
  return { top: px(app.safeAreaInset?.top), height };
}

const sameBand = (a: TelegramTopBand | null, b: TelegramTopBand | null) =>
  a === b || (a !== null && b !== null && a.top === b.top && a.height === b.height);

/**
 * Founder, 11 Oct 2026 (#2176): in full screen the app's title lives in the
 * middle of Telegram's button band. This hook says whether there is such a
 * band and where, and follows Telegram's full-screen / inset changes. `null`
 * outside Telegram (`active` false), and in Telegram when not full screen
 * (Desktop, Web, older clients) — the app's own header stays then.
 */
export function useTelegramTopBand(active: boolean): TelegramTopBand | null {
  const [band, setBand] = useState<TelegramTopBand | null>(() =>
    active ? telegramTopBand(telegramWebApp()) : null,
  );
  useEffect(() => {
    if (!active) return;
    let app: TelegramWebApp | null = null;
    const update = () => {
      const next = telegramTopBand(app);
      setBand((prev) => (sameBand(prev, next) ? prev : next));
    };
    const cancel = whenTelegramWebApp((loaded) => {
      app = loaded;
      update();
      for (const event of EVENTS) loaded.onEvent?.(event, update);
    });
    return () => {
      cancel();
      for (const event of EVENTS) app?.offEvent?.(event, update);
    };
  }, [active]);
  return active ? band : null;
}
